import { useState } from 'react';

import { XAPISession } from './useXAPISession';
import { xapiClient } from '@/store/xapi/client';
import {
  ActivityDefinitionType,
  ResultProps,
  Statement,
  statementCompleted,
  statementProgressed,
  StatementId,
  statementQuestionAnswered,
  statementRegistered,
} from '@/store/xapi/model';
import { useCurrentUser } from '@/store/currentUser';

export type UseXAPIClientHook = (xapiSession?: XAPISession) => XAPIClient;

export type XAPIClient = {
  setLearningCompleted(learningId: number, moduleName: string): void;
  // progress should be between 0 - 100
  setLearningProgressed(learningId: number, moduleName: string, progress: number): void;
  setLearningModuleCompleted(moduleId: number, moduleName: string): void;
  setLearningPageCompleted(pageId: number, moduleName: string): void;
  setLearningActivityRegistred(activityId: number, activityName: string): void;
  setLearningActivityCompleted(pageId: number, moduleName: string): void;
  setLearningQuestionAnswer(input: SetLearningQuestionAnswerInput): void;
  isInitialized: boolean;
  loading: boolean;
};

type SetLearningQuestionAnswerInput = {
  learningObject: {
    id: number;
    name: string;
  };
  questionObject: {
    id: number;
    name: string;
    type: ActivityDefinitionType;
  };
  questionAnswer: ResultProps;
};

export const useXAPIClient: UseXAPIClientHook = (session?: XAPISession) => {
  const [statementIdsSent, setStatementIdsSent] = useState<Set<StatementId>>(new Set([]));
  const { user, loading: loadingUser } = useCurrentUser();

  const sendStatement = async (statement: Statement) => {
    if (!statement.id || statementIdsSent.has(statement.id) || !session) return;

    try {
      await xapiClient.postStatement(session, [statement]);

      setStatementIdsSent((sent) => new Set([...sent, statement.id as StatementId]));
    } catch (e) {
      console.error(`Failed to send statement ${statement.id}: ${e}`);
    }
  };

  return {
    setLearningCompleted: (id, name) =>
      sendStatement(
        statementCompleted({
          userId: user?.id || 0,
          objectId: `urn:collegial:${id}`,
          objectName: name,
          timestamp: new Date(),
          registration: session?.registration,
        }),
      ),
    setLearningProgressed: (id, name, progress) =>
      sendStatement(
        statementProgressed({
          userId: user?.id || 0,
          objectId: `urn:collegial:${id}`,
          objectName: name,
          timestamp: new Date(),
          registration: session?.registration,
          progress,
        }),
      ),
    setLearningModuleCompleted: (id, name) =>
      sendStatement(
        statementCompleted({
          userId: user?.id || 0,
          objectId: `urn:collegial:module:${id}`,
          objectName: name,
          timestamp: new Date(),
          registration: session?.registration,
        }),
      ),
    setLearningPageCompleted: (id, name) =>
      sendStatement(
        statementCompleted({
          userId: user?.id || 0,
          objectId: `urn:collegial:page:${id}`,
          objectName: name,
          timestamp: new Date(),
          registration: session?.registration,
        }),
      ),
    setLearningActivityRegistred: (id, name) =>
      sendStatement(
        statementRegistered({
          userId: user?.id || 0,
          objectId: `urn:collegial:self:learning-activity:${id}`,
          objectName: name,
          timestamp: new Date(),
          registration: session?.registration,
        }),
      ),
    setLearningActivityCompleted: (id, name) =>
      sendStatement(
        statementCompleted({
          userId: user?.id || 0,
          objectId: `urn:collegial:self:learning-activity:${id}`,
          objectName: name,
          timestamp: new Date(),
          registration: session?.registration,
        }),
      ),
    setLearningQuestionAnswer: (input: SetLearningQuestionAnswerInput) =>
      sendStatement(
        statementQuestionAnswered({
          userId: user?.id || 0,
          learningObject: {
            id: `urn:collegial:${input.learningObject.id}`,
            name: input.learningObject.name,
          },
          questionObject: {
            id: `urn:collegial:page:${input.questionObject.id}`,
            name: input.questionObject.name,
            type: input.questionObject.type,
          },
          questionAnswer: input.questionAnswer,
          timestamp: new Date(),
          registration: session?.registration || '',
        }),
      ),
    isInitialized: !!session,
    loading: loadingUser,
  };
};
